import classNames from 'classnames';
import { useState } from 'react';
import { useTimeoutFn } from 'react-use';
import styled from 'styled-components';

import { AnimatedNumber } from 'atoms/animations/AnimatedNumber';
import { EmojiFlood } from 'atoms/animations/EmojiFlood';
import { fadeIn } from 'atoms/animations/keyframes';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { tabletAndAbove } from 'style/mediaQuery';

import { MinigamePopup } from '../MinigamePopup';

type GameResult = 'Win' | 'Lose' | 'Tie';

type Props = {
  yourTeamScore: number;
  opponentTeamScore: number;
  showResult: boolean;
  onClick: () => void;
  popupBackground: string;
};

const ResultRoot = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  flex: 1;
  z-index: 1;
  justify-content: space-around;
  transform-origin: center;
  opacity: 0;
  pointer-events: none;
  &.showResult {
    pointer-events: auto;
    animation: 0.3s ease ${fadeIn} forwards;
  }
`;

const ScoreTitle = styled(Horizontal).attrs({ gap: 0, center: true })`
  font-size: 60px;
  line-height: 1;
  text-transform: uppercase;
  font-family: var(--brand-font);
  z-index: 1;
  @media ${tabletAndAbove} {
    font-size: 80px;
  }
`;

export const Result = ({
  yourTeamScore,
  opponentTeamScore,
  showResult,
  onClick,
  popupBackground,
}: Props) => {
  const [displayResult, setDisplayResult] = useState(false);

  useTimeoutFn(() => {
    setDisplayResult(true);
  }, 3000);

  let result: GameResult;
  if (yourTeamScore > opponentTeamScore) {
    result = 'Win';
  } else if (yourTeamScore === opponentTeamScore) {
    result = 'Tie';
  } else {
    result = 'Lose';
  }

  return (
    <ResultRoot className={classNames({ showResult })}>
      <ScoreTitle>
        <AnimatedNumber
          value={opponentTeamScore}
          animationProps={{ config: { duration: 3000 } }}
        />
      </ScoreTitle>
      <MinigamePopup
        onConfirm={onClick}
        popupBackground={popupBackground}
        result={result}
        displayResult={displayResult}
      />

      <ScoreTitle>
        <AnimatedNumber
          value={yourTeamScore}
          animationProps={{ config: { duration: 3000 } }}
        />
      </ScoreTitle>
      <div style={{ pointerEvents: 'none' }}>
        {!!displayResult && (
          <EmojiFlood emoji={yourTeamScore > opponentTeamScore ? '🔥' : '🤣'} />
        )}
      </div>
    </ResultRoot>
  );
};
